/**
 * 工作流画布快捷键处理器 Hook
 *
 * 统一管理工作流画布的所有快捷键操作
 * 参考: docs/ragflow_web 工作流设计
 */

import { useEffect, useCallback, useRef } from 'react';
import { useReactFlow, type Node } from '@xyflow/react';
import { useWorkflowStore } from '../store/workflowStore';
import { useExecutionStore } from '../store/workflowStore';
import { ExecutionState, type WorkflowNode } from '../types/workflow';

/** 快捷键配置 */
interface ShortcutConfig {
  /** 是否启用节点操作快捷键 */
  enableNodeShortcuts?: boolean;
  /** 是否启用画布快捷键 */
  enableCanvasShortcuts?: boolean;
  /** 是否启用调试快捷键 */
  enableDebugShortcuts?: boolean;
  /** 是否启用选择快捷键 */
  enableSelectionShortcuts?: boolean;
  /** 自定义保存回调 */
  onSave?: () => void;
  /** 自定义布局回调 */
  onAutoLayout?: () => void;
}

/** 复制的节点数据 */
interface CopiedNodes {
  nodes: Node[];
  timestamp: number;
}

// 全局复制缓存（支持跨组件粘贴）
let globalCopiedNodes: CopiedNodes | null = null;

/**
 * 工作流快捷键处理器 Hook
 */
export function useShortcutKeys(config: ShortcutConfig = {}) {
  const {
    enableNodeShortcuts = true,
    enableCanvasShortcuts = true,
    enableDebugShortcuts = true,
    enableSelectionShortcuts = true,
    onSave,
    onAutoLayout,
  } = config;

  const reactFlowInstance = useReactFlow();

  // 使用 ref 存储最新的 store 状态，避免闭包问题
  const workflowStoreRef = useRef(useWorkflowStore.getState());
  const executionStoreRef = useRef(useExecutionStore.getState());

  // 更新 ref
  useEffect(() => {
    const unsubscribeWorkflow = useWorkflowStore.subscribe((state) => {
      workflowStoreRef.current = state;
    });
    const unsubscribeExecution = useExecutionStore.subscribe((state) => {
      executionStoreRef.current = state;
    });

    return () => {
      unsubscribeWorkflow();
      unsubscribeExecution();
    };
  }, []);

  // ==================== 节点操作快捷键 ====================

  // 复制选中节点
  const copySelectedNodes = useCallback(() => {
    const { nodes } = workflowStoreRef.current;
    const selectedNodes = nodes.filter((n) => n.selected);

    if (selectedNodes.length === 0) return;

    globalCopiedNodes = {
      nodes: selectedNodes.map((n) => ({ ...n, selected: false })),
      timestamp: Date.now(),
    };

    // 可以在这里添加 toast 提示
    console.log(`已复制 ${selectedNodes.length} 个节点`);
  }, []);

  // 粘贴节点
  const pasteNodes = useCallback(() => {
    if (!globalCopiedNodes || globalCopiedNodes.nodes.length === 0) return;

    const offset = 20; // 粘贴偏移量

    // 计算边界框，确定粘贴位置
    const copiedNodes = globalCopiedNodes.nodes;
    let minX = Infinity;
    let minY = Infinity;

    copiedNodes.forEach((n) => {
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
    });

    // 创建新节点
    const newNodeIds: string[] = [];
    const newNodes = copiedNodes.map((n) => {
      const newId = `${n.type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      newNodeIds.push(newId);
      return {
        ...n,
        id: newId,
        position: {
          x: n.position.x - minX + offset,
          y: n.position.y - minY + offset,
        },
        data: {
          ...n.data,
          label: n.data.label ? `${n.data.label} (副本)` : '副本',
        },
        selected: false,
      } as WorkflowNode;
    });

    // 更新 store
    useWorkflowStore.setState((state) => {
      state.nodes = [...state.nodes, ...newNodes];
      state.isDirty = true;
    });

    // 选中新粘贴的节点
    setTimeout(() => {
      useWorkflowStore.setState((state) => {
        state.nodes = state.nodes.map((n) => ({
          ...n,
          selected: newNodeIds.includes(n.id),
        }));
      });
    }, 0);

    console.log(`已粘贴 ${newNodes.length} 个节点`);
  }, []);

  // 复制节点（快捷复制，不需要粘贴）
  const duplicateSelectedNodes = useCallback(() => {
    const store = workflowStoreRef.current;
    const selectedNodes = store.nodes.filter((n) => n.selected);

    if (selectedNodes.length === 0) return;

    const offset = 20;
    const newNodeIds: string[] = [];

    useWorkflowStore.setState((state) => {
      const newNodes = selectedNodes.map((n) => {
        const newId = `${n.type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        newNodeIds.push(newId);
        return {
          ...n,
          id: newId,
          position: {
            x: n.position.x + offset,
            y: n.position.y + offset,
          },
          data: {
            ...n.data,
            label: n.data.label ? `${n.data.label} (副本)` : '副本',
          },
          selected: false,
        };
      });

      state.nodes = [...state.nodes, ...newNodes];
      state.isDirty = true;
    });

    // 选中新复制的节点
    setTimeout(() => {
      useWorkflowStore.setState((state) => {
        state.nodes = state.nodes.map((n) => ({
          ...n,
          selected: newNodeIds.includes(n.id),
        }));
      });
    }, 0);

    console.log(`已复制 ${selectedNodes.length} 个节点`);
  }, []);

  // 删除选中节点
  const deleteSelectedNodes = useCallback(() => {
    const store = workflowStoreRef.current;
    const selectedNodes = store.nodes.filter((n) => n.selected);

    if (selectedNodes.length === 0) return;

    const selectedIds = selectedNodes.map((n) => n.id);

    useWorkflowStore.setState((state) => {
      // 删除节点
      state.nodes = state.nodes.filter((n) => !selectedIds.includes(n.id));
      // 删除相关的边
      state.edges = state.edges.filter(
        (e) => !selectedIds.includes(e.source) && !selectedIds.includes(e.target)
      );
      // 清除选中状态
      if (selectedIds.includes(state.selectedNodeId || '')) {
        state.selectedNodeId = null;
      }
      state.isDirty = true;
    });

    console.log(`已删除 ${selectedNodes.length} 个节点`);
  }, []);

  // ==================== 画布操作快捷键 ====================

  // 适应画布
  const fitView = useCallback(() => {
    reactFlowInstance.fitView({ padding: 0.2, duration: 200 });
  }, [reactFlowInstance]);

  // 放大
  const zoomIn = useCallback(() => {
    reactFlowInstance.zoomIn({ duration: 200 });
  }, [reactFlowInstance]);

  // 缩小
  const zoomOut = useCallback(() => {
    reactFlowInstance.zoomOut({ duration: 200 });
  }, [reactFlowInstance]);

  // 重置缩放
  const resetZoom = useCallback(() => {
    reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 200 });
  }, [reactFlowInstance]);

  // 自动布局
  const autoLayout = useCallback(() => {
    if (onAutoLayout) {
      onAutoLayout();
      return;
    }

    // 默认的简单布局算法
    const store = workflowStoreRef.current;
    const { nodes } = store;

    if (nodes.length === 0) return;

    // 简单的网格布局
    const nodeWidth = 240;
    const nodeHeight = 100;
    const gapX = 40;
    const gapY = 60;
    const cols = Math.ceil(Math.sqrt(nodes.length));

    const newNodes = nodes.map((node, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      return {
        ...node,
        position: {
          x: col * (nodeWidth + gapX),
          y: row * (nodeHeight + gapY),
        },
      };
    });

    useWorkflowStore.setState((state) => {
      state.nodes = newNodes;
      state.isDirty = true;
    });

    // 布局后适应画布
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2, duration: 200 });
    }, 50);

    console.log('已自动布局');
  }, [reactFlowInstance, onAutoLayout]);

  // 保存工作流
  const saveWorkflow = useCallback(() => {
    if (onSave) {
      onSave();
      return;
    }

    // 默认保存逻辑：标记为已保存
    useWorkflowStore.getState().markClean();
    console.log('工作流已保存');
  }, [onSave]);

  // ==================== 调试操作快捷键 ====================

  // 开始执行
  const startExecution = useCallback(() => {
    const store = executionStoreRef.current;
    const state = store.executionState;

    if (state === ExecutionState.IDLE ||
        state === ExecutionState.COMPLETED ||
        state === ExecutionState.FAILED) {
      useExecutionStore.getState().setExecutionState(ExecutionState.RUNNING);
      useExecutionStore.getState().setError(null);
      console.log('开始执行');
    }
  }, []);

  // 暂停/恢复执行
  const togglePauseExecution = useCallback(() => {
    const store = executionStoreRef.current;
    const state = store.executionState;

    if (state === ExecutionState.RUNNING) {
      useExecutionStore.getState().setExecutionState(ExecutionState.PAUSED);
      console.log('已暂停');
    } else if (state === ExecutionState.PAUSED) {
      useExecutionStore.getState().setExecutionState(ExecutionState.RUNNING);
      console.log('已恢复');
    } else if (state === ExecutionState.STEP_WAITING) {
      // 从单步等待恢复
      useExecutionStore.getState().setExecutionState(ExecutionState.RUNNING);
      console.log('继续执行');
    }
  }, []);

  // 单步执行
  const stepExecution = useCallback(() => {
    const store = executionStoreRef.current;
    const state = store.executionState;

    if (state === ExecutionState.IDLE ||
        state === ExecutionState.COMPLETED ||
        state === ExecutionState.FAILED) {
      // 开始单步模式
      useExecutionStore.getState().setStepMode(true);
      useExecutionStore.getState().setExecutionState(ExecutionState.RUNNING);
      useExecutionStore.getState().setError(null);
      console.log('开始单步执行');
    } else if (state === ExecutionState.STEP_WAITING) {
      // 执行下一步
      useExecutionStore.getState().setExecutionState(ExecutionState.RUNNING);
      console.log('执行下一步');
    } else if (state === ExecutionState.PAUSED) {
      // 从暂停恢复为单步
      useExecutionStore.getState().setStepMode(true);
      useExecutionStore.getState().setExecutionState(ExecutionState.RUNNING);
      console.log('单步继续');
    }
  }, []);

  // 中断执行
  const interruptExecution = useCallback(() => {
    const store = executionStoreRef.current;
    const state = store.executionState;

    if (state === ExecutionState.RUNNING ||
        state === ExecutionState.PAUSED ||
        state === ExecutionState.STEP_WAITING) {
      useExecutionStore.getState().setExecutionState(ExecutionState.INTERRUPTED);
      console.log('已中断执行');
    }
  }, []);

  // ==================== 选择操作快捷键 ====================

  // 全选节点
  const selectAllNodes = useCallback(() => {
    useWorkflowStore.setState((state) => {
      state.nodes.forEach((n) => {
        n.selected = true;
      });
    });
    console.log('已全选节点');
  }, []);

  // 取消选择
  const deselectAll = useCallback(() => {
    useWorkflowStore.setState((state) => {
      state.nodes.forEach((n) => {
        n.selected = false;
      });
      state.edges.forEach((e) => {
        e.selected = false;
      });
      state.selectedNodeId = null;
    });
  }, []);

  // 反选
  const invertSelection = useCallback(() => {
    useWorkflowStore.setState((state) => {
      state.nodes.forEach((n) => {
        n.selected = !n.selected;
      });
    });
  }, []);

  // ==================== 键盘事件处理 ====================

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // 忽略在输入框中的快捷键
      const target = event.target as HTMLElement;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // 对于某些快捷键，即使在输入框中也应该响应
      const isGlobalShortcut =
        event.key === 'Escape' ||
        event.key === 'F5' ||
        event.key === 'F10';

      if (isInputElement && !isGlobalShortcut) {
        return;
      }

      const { key, ctrlKey, metaKey, shiftKey } = event;
      const isCtrlOrCmd = ctrlKey || metaKey;

      // ==================== 节点操作快捷键 ====================
      if (enableNodeShortcuts) {
        // Ctrl+C: 复制
        if (isCtrlOrCmd && key === 'c') {
          event.preventDefault();
          copySelectedNodes();
          return;
        }

        // Ctrl+V: 粘贴
        if (isCtrlOrCmd && key === 'v') {
          event.preventDefault();
          pasteNodes();
          return;
        }

        // Ctrl+D: 快捷复制
        if (isCtrlOrCmd && key === 'd') {
          event.preventDefault();
          duplicateSelectedNodes();
          return;
        }

        // Delete/Backspace: 删除（由 React Flow 内置处理）
        // 这里我们额外处理，确保有日志记录
        if (key === 'Delete' || key === 'Backspace') {
          // React Flow 已经处理了删除，这里只是记录日志
          const store = workflowStoreRef.current;
          const selectedNodes = store.nodes.filter((n) => n.selected);
          if (selectedNodes.length > 0) {
            console.log(`删除 ${selectedNodes.length} 个节点`);
          }
          return;
        }
      }

      // ==================== 画布操作快捷键 ====================
      if (enableCanvasShortcuts) {
        // Ctrl+0: 适应画布
        if (isCtrlOrCmd && key === '0') {
          event.preventDefault();
          fitView();
          return;
        }

        // Ctrl+L: 自动布局
        if (isCtrlOrCmd && key === 'l') {
          event.preventDefault();
          autoLayout();
          return;
        }

        // Ctrl+S: 保存
        if (isCtrlOrCmd && key === 's') {
          event.preventDefault();
          saveWorkflow();
          return;
        }

        // Ctrl+= 或 Ctrl++: 放大
        if (isCtrlOrCmd && (key === '=' || key === '+')) {
          event.preventDefault();
          zoomIn();
          return;
        }

        // Ctrl+-: 缩小
        if (isCtrlOrCmd && key === '-') {
          event.preventDefault();
          zoomOut();
          return;
        }

        // Ctrl+1: 重置缩放
        if (isCtrlOrCmd && key === '1') {
          event.preventDefault();
          resetZoom();
          return;
        }
      }

      // ==================== 调试操作快捷键 ====================
      if (enableDebugShortcuts) {
        // F5: 开始执行
        if (key === 'F5') {
          event.preventDefault();
          startExecution();
          return;
        }

        // F10: 单步执行
        if (key === 'F10') {
          event.preventDefault();
          stepExecution();
          return;
        }

        // Space: 暂停/恢复（需要确保不在输入框中）
        if (key === ' ' && !isInputElement) {
          event.preventDefault();
          togglePauseExecution();
          return;
        }

        // Escape: 中断执行
        if (key === 'Escape') {
          event.preventDefault();
          interruptExecution();
          return;
        }
      }

      // ==================== 选择操作快捷键 ====================
      if (enableSelectionShortcuts) {
        // Ctrl+A: 全选
        if (isCtrlOrCmd && key === 'a') {
          event.preventDefault();
          selectAllNodes();
          return;
        }

        // Ctrl+Shift+A: 取消选择
        if (isCtrlOrCmd && shiftKey && key === 'A') {
          event.preventDefault();
          deselectAll();
          return;
        }

        // Ctrl+I: 反选
        if (isCtrlOrCmd && key === 'i') {
          event.preventDefault();
          invertSelection();
          return;
        }
      }
    },
    [
      enableNodeShortcuts,
      enableCanvasShortcuts,
      enableDebugShortcuts,
      enableSelectionShortcuts,
      copySelectedNodes,
      pasteNodes,
      duplicateSelectedNodes,
      fitView,
      autoLayout,
      saveWorkflow,
      zoomIn,
      zoomOut,
      resetZoom,
      startExecution,
      stepExecution,
      togglePauseExecution,
      interruptExecution,
      selectAllNodes,
      deselectAll,
      invertSelection,
    ]
  );

  // 注册全局键盘事件
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // 返回快捷键操作方法，供外部调用
  return {
    // 节点操作
    copySelectedNodes,
    pasteNodes,
    duplicateSelectedNodes,
    deleteSelectedNodes,

    // 画布操作
    fitView,
    zoomIn,
    zoomOut,
    resetZoom,
    autoLayout,
    saveWorkflow,

    // 调试操作
    startExecution,
    stepExecution,
    togglePauseExecution,
    interruptExecution,

    // 选择操作
    selectAllNodes,
    deselectAll,
    invertSelection,
  };
}

export default useShortcutKeys;
